import React, { useEffect, useState, useContext, useRef } from "react";
import { EARLY_PAY, discountBase } from "helpers/earlyPay";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon, VersionIcon, NotesIcon, ItemsIcon, OverviewIcon, PaymentsIcon } from "components/svg";
import H2 from "components/typography/H2";
import { themevals } from "theme/themevals";
import H5 from "components/typography/H5";
import Overview from "./components/Overview";
// import Versions from "./components/Versions";
import Notes from "./components/Notes";
import CorrigoInvoiceSubmissionPanel from "./components/CorrigoInvoiceSubmissionPanel";
import UserContext from "context/User";
import InvoiceStatusBadge from "components/ui/InvoiceStatusBadge";
import { getMyCompany } from "api/Companies";
import Payments from "./components/Payments";
import { getInvoice, updateInvoice, applyInvoicePaymentDiscount } from "api/Invoices";
import { trackActivity } from "api/ActivityTracker";
import P from "components/typography/P";
import moment from "moment-timezone";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import { getOutstandingDeposits, applyDepositCredit } from "api/QuoteExpiration";
import SiteMapSection from "components/SiteMapSection";
import SnapshotSelectionDisplay from "components/SnapshotSelectionDisplay";
import MaintenanceContextBanner from "components/MaintenanceContextBanner";

const Invoice = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const { user } = useContext<any>(UserContext);
  const [company, setCompany] = useState<any>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [confirmVoidModalOpen, setConfirmVoidModalOpen] = useState(false);
  const [confirmDiscountOpen, setConfirmDiscountOpen] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [applyingCreditId, setApplyingCreditId] = useState<string | null>(null);
  const toast = useToast();
  const hasTrackedView = useRef(false);
    useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Invoice";
    hasTrackedView.current = false;
    _init();
  }, [id]);

  useEffect(() => {
    const tab = searchParams.get("tab") || "overview";
    setActiveTab(tab);
  }, [searchParams]);

  const _init = async () => {
    if (user.role !== null) {
      const res = await getMyCompany();
      if (res.id) {
        setCompany(res);
      }
    } else {
      setCompany(user.company);
    }

    const invoiceRes = await getInvoice({ data: { id: id } });
    if (invoiceRes.id) {
      setInvoice(invoiceRes);
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "invoices",
          action: "view_invoice_detail",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "invoice",
          entityId: invoiceRes.id,
          metadata: null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }
  };

  useEffect(() => {
  // Only run if invoice is loaded, has a discount, and has no payments
  if (
    invoice &&
    invoice.discount &&
    invoice.dueDate &&
    invoice.client &&
    typeof invoice.client.terms === "number" &&
    (!Array.isArray(invoice.payments) || invoice.payments.length === 0)
  ) {
    _updateDiscount();
  }
  // eslint-disable-next-line
}, [invoice]);

  // Fetch outstanding deposit credits for this invoice's client
  useEffect(() => {
    if (!invoice?.client?.id) return;
    // Only show for unpaid invoices (status 1 = sent)
    if (invoice.status !== 1) { setDeposits([]); return; }
    const fetchDeposits = async () => {
      // Get parent client ID for org clients
      const clientId = invoice.client?.parentId || invoice.client?.id;
      const result = await getOutstandingDeposits(clientId);
      setDeposits(Array.isArray(result) ? result : []);
    };
    fetchDeposits();
  }, [invoice?.id, invoice?.status, invoice?.client?.id]);

  const handleApplyCredit = async (quoteId: string, depositAmount: number) => {
    if (!id) return;
    setApplyingCreditId(quoteId);
    try {
      const res = await applyDepositCredit(quoteId, id);
      if (res?.success) {
        toast.add('Deposit credit of ' + '\u0024' + Number(depositAmount).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' applied to this invoice.', 'var(--theme-ui-colors-success)');
        setDeposits(prev => prev.filter(d => d.quoteId !== quoteId));
        reloadInvoice();
      } else {
        toast.add(res?.message || 'Failed to apply credit.', 'var(--theme-ui-colors-danger)');
      }
    } catch (e: any) {
      toast.add('Error applying deposit credit.', 'var(--theme-ui-colors-danger)');
    }
    setApplyingCreditId(null);
  };

  /**
   * STAFF OVERRIDE for the early-payment discount.
   *
   * The discount normally applies itself at payment, and only when the
   * invoice clears every rule — $2,000 minimum, within 5 days of quote
   * acceptance, not a rush order, no deposit required. This grants it
   * anyway, for the case where the team decides to (GMI-100559 missed the
   * minimum by 50 cents). The backend recomputes the amount and syncs it
   * to Zoho; nothing here is trusted beyond the preview.
   */
  const canApplyDiscount =
    user.role !== null &&
    invoice != null &&
    invoice.status < 2 &&
    !(Number(invoice.discountAmount) > 0) &&
    !(Array.isArray(invoice.payments) && invoice.payments.length > 0);

  /**
   * Is the early-payment offer actually open on this invoice? The SAME rule
   * the invoice document (Overview) and the PDF use: within 5 days of quote
   * acceptance, $2,000 minimum, not a rush order, and the client is NOT on
   * the deposit requirement. The Due Date field used to show "Pay deposit by
   * ... for a discount" on every invoice without a due date, including
   * deposit-required clients who can never get it (GMI-100566, 2026-10-01).
   */
  const earlyOffer = (() => {
    const accepted = invoice?.quote?.acceptedDate;
    if (!accepted) return { eligible: false, expiryText: "" };
    const expiry = moment(accepted).clone().add(5, "days");
    const total =
      invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + Number(item.price) * Number(item.quantity), 0) ||
      Number(invoice?.total ?? 0);
    // Truthy, not === true: these can arrive as 1 from MariaDB.
    const requiresDeposit =
      !!(invoice as any)?.client?.requiresDeposit ||
      !!(invoice as any)?.primaryClient?.requiresDeposit ||
      !!(invoice as any)?.quote?.requiresDeposit ||
      !!(invoice as any)?.quote?.earlyPaymentDiscountOff;
    const rush = !!invoice?.project?.isRushOrder;
    return {
      eligible: moment().isBefore(expiry) && total >= 2000 && !rush && !requiresDeposit,
      expiryText: expiry.format("MMM D, YYYY"),
    };
  })();

  // Preview only — same integer-cent, half-up rounding as the backend, so
  // the figure in the dialog is the figure the client will be charged.
  const discountPreview = (() => {
    const totalCents = Math.round(Number(invoice?.total ?? 0) * 100);
    // Goods only: the fuel surcharge is never discounted (backend matches).
    const offCents = Math.round(discountBase((invoice as any)?.items) * EARLY_PAY.fullPct);
    return { off: offCents / 100, after: (totalCents - offCents) / 100 };
  })();

  const _applyDiscount = async () => {
    if (!invoice?.id) return;
    setApplyingDiscount(true);
    const res = await applyInvoicePaymentDiscount(invoice.id);
    setApplyingDiscount(false);
    setConfirmDiscountOpen(false);
    if (res?.id) {
      setInvoice(res);
      toast.add("Payment discount applied and synced to Zoho.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message || "Could not apply the discount.", "var(--theme-ui-colors-danger)");
    }
  };

  const confirmVoidInvoice = async () => {
    setConfirmVoidModalOpen(true);
  };

  const reloadInvoice = async () => {
    _init();
  };

  const _cancelConfirmModal = () => {
    setConfirmVoidModalOpen(false);
  };

  const _voidInvoice = async () => {
    setSubmitting(true);
    const res = await updateInvoice({ data: { id: id }, update: { status: 4 } });
    if (res.id) {
      setSubmitting(false);
      setConfirmVoidModalOpen(false);
      setInvoice(res);
      toast.add(`Invoice voided.`, "var(--theme-ui-colors-success)");
    } else {
      setSubmitting(false);
      setConfirmVoidModalOpen(false);
      toast.add(`Error voiding invoice.`, "var(--theme-ui-colors-danger)");
    }
  };

  const _updateDiscount = async () => {
    if (invoice && invoice.discount && invoice.dueDate && invoice.client && typeof invoice.client.terms === "number") {
      const today = moment().startOf("day");
      const dueDate = moment(invoice.dueDate).startOf("day");
      const daysLeft = dueDate.diff(today, "days");
      const halfTerms = Math.floor(invoice.client.terms / 2);

      if (daysLeft <= halfTerms) {
        // Remove discount fields
        const res = await updateInvoice({
          data: { id: invoice.id },
          update: {
            discount: null,
            discountAmount: null,
            discountTotal: null,
          },
        });
        if (res.id) {
          setInvoice(res);
          toast.add("Discount removed due to approaching due date.", "var(--theme-ui-colors-warning)");
        }
      }
    }
  };

  return (
    <>
      <Header></Header>
      <Page
        backAction={
          <Link variant="back" href="/invoices">
            <BackArrowIcon />
            {`Back to ${"Invoices"}`}
          </Link>
        }
        $noPadding={true}
      >
        {invoice == null ? (
          <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : (
          <>
            {/* Deposit Credit Banner */}
            {deposits.length > 0 && (
              <div style={{ backgroundColor: '#E6FFFA', border: '1px solid #81E6D9', borderRadius: '8px', padding: '14px 20px', marginBottom: '16px', fontFamily: 'Poppins, sans-serif' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: deposits.length > 1 ? '10px' : '0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#38B2AC', flexShrink: 0 }}></span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#234E52' }}>Deposit Credit Available</span>
                </div>
                {deposits.map((dep: any) => (
                  <div key={dep.quoteId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: deposits.length > 1 ? '8px 0' : '6px 0 0', borderTop: deposits.length > 1 ? '1px solid #B2F5EA' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '13px', color: '#234E52' }}>
                        {'\u0024' + Number(dep.depositAmount).toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' from '}
                        <strong>{'GMQ-' + String(dep.sequence).padStart(5, '0')}</strong>
                        {dep.clientName ? ' (' + dep.clientName + ')' : ''}
                        {dep.depositMethod ? ' via ' + dep.depositMethod : ''}
                      </span>
                    </div>
                    <BasicButton
                      scheme="primary"
                      onClick={() => handleApplyCredit(dep.quoteId, dep.depositAmount)}
                      $submitting={applyingCreditId === dep.quoteId}
                      $loadingColor="#fff"
                      keepText={true}
                      styles={{ fontSize: '11px', padding: '4px 12px' }}
                    >
                      Apply to Invoice
                    </BasicButton>
                  </div>
                ))}
              </div>
            )}
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              <Box>
                <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                  Invoice Details
                </H5>
              </Box>
              <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
                <Flex sx={{ gap: "40px" }}>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "overview" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("overview");
                      setSearchParams({ tab: "overview" });
                    }}
                  >
                    <OverviewIcon fill={activeTab == "overview" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "overview" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Overview
                    </Box>
                  </Flex>
                  {/*
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "versions" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("versions");
                      setSearchParams({ tab: "versions" });
                    }}
                  >
                    <VersionIcon fill={activeTab == "versions" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "versions" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Versions
                    </Box>
                  </Flex>
                  */}
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "notes" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("notes");
                      setSearchParams({ tab: "notes" });
                    }}
                  >
                    <NotesIcon fill={activeTab == "notes" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "notes" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Notes
                    </Box>
                  </Flex>
                <Flex
                  sx={{ paddingBottom: "10px", borderBottom: activeTab == "payments" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                  onClick={() => {
                    setActiveTab("payments");
                    setSearchParams({ tab: "payments" });
                    void trackActivity({
                      module: "invoices",
                      action: "view_invoice_payments",
                      actionType: "view",
                      userId: user?.id || null,
                      companyId: user?.company?.id || null,
                      entityType: "invoice",
                      entityId: invoice?.id || null,
                      metadata: null,
                      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                    });
                  }}
                >
                  <PaymentsIcon fill={activeTab == "payments" ? "#000" : themevals.colors.dkgrey} width="14px" />
                  <Box sx={{ color: activeTab == "payments" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                    Payments
                    </Box>
                  </Flex>
                  {/*
                   * A22 (mockup 22) — Corrigo submission is its own tab,
                   * not a sidebar cram. Same gate as the old sidebar
                   * mount: staff only, and only when this invoice's RFQ
                   * is plan-tagged (a Corrigo/WO-fed invoice). Mirrors the
                   * quote-side CorrigoExportPanel tab. `?tab=corrigo` deep
                   * link comes free from the existing tab machinery.
                   */}
                  {user.role !== null && invoice.quote?.quoteRequest?.servicePlanId && (
                    <Flex
                      sx={{ paddingBottom: "10px", borderBottom: activeTab == "corrigo" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                      onClick={() => {
                        setActiveTab("corrigo");
                        setSearchParams({ tab: "corrigo" });
                      }}
                    >
                      <OverviewIcon fill={activeTab == "corrigo" ? "#000" : themevals.colors.dkgrey} width="14px" />
                      <Box sx={{ color: activeTab == "corrigo" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                        Post to Corrigo
                      </Box>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Grid>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              {/* See Projects/view.tsx: minWidth 0 + overflowWrap on the
                  sidebar column so emails and URLs wrap inside 250px. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
                {user.role !== null && invoice.status < 2 && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                    <Link
                      href={"/invoices/" + invoice.id + "/edit"}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        width: "100%", padding: "10px 0",
                        border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                        textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                        fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                      }}
                    >
                      <PencilIcon height="13px" fill="currentColor" />
                      Edit
                    </Link>
                  </Box>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Basic Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      <InvoiceStatusBadge invoice={invoice} />
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project Name</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {invoice.project?.projectName ? invoice.project?.projectName : "Instant Estimate"}
                    </P>
                  </Flex>
                  {user.role !== null && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Company</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {invoice.client?.name}
                      </P>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Quote #</Label>
                    <Box
                      onClick={() => {
                        navigate(`/quotes/${invoice.quote.id}`);
                      }}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {invoice.quote?.sequence ? `GMQ-${String(invoice.quote?.sequence).padStart(5, "0")}` : "N/A"}
                      </P>
                    </Box>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project #</Label>
                    <Box
                      onClick={() => {
                        navigate(`/projects/${invoice.project.id}`);
                      }}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {invoice.project?.sequence ? `GMP-${String(invoice.project?.sequence).padStart(5, "0")}` : "N/A"}
                      </P>
                    </Box>
                  </Flex>
                  {/* Install-only: mulch supply preference, read from the RFQ via the
                      quote. Structured field only; supply wording rides on the
                      install-only service description in the line-item table. */}
                  {invoice.quote?.quoteRequest?.mulchSupplyPreference && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Mulch Supply</Label>
                      <Box sx={{ marginTop: "5px" }}>
                        <Badge as="span" variant="info">
                          {invoice.quote.quoteRequest.mulchSupplyPreference === "pickup" ? "Pickup" : "Delivery"}
                        </Badge>
                      </Box>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Created At</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(invoice.createdAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Due Date</Label>
                    {invoice.dueDate ? (
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {moment(invoice.dueDate).format("MMM D, YYYY")}
                      </P>
                    ) : (!invoice.discountAmount || invoice.discountAmount === 0) && earlyOffer.eligible ? (
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {`Pay deposit by ${earlyOffer.expiryText} for a discount`}
                      </P>
                    ) : (
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        -
                      </P>
                    )}
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Sent Date</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {invoice.sentDate ? moment(invoice.sentDate).format("MMM D, YYYY") : "-"}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Updated</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(invoice.updatedAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  {invoice.lastUpdateBy?.firstName && invoice.lastUpdateBy?.lastName && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Updated By</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {`${invoice.lastUpdateBy.firstName} ${invoice.lastUpdateBy.lastName}`}
                      </P>
                    </Flex>
                  )}
                </Box>
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Address
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {invoice.project.location?.address?.line1 && invoice.project.location?.address?.line1 !== invoice.project.location?.address?.city
                        ? invoice.project.location?.address?.line1 + (invoice.project.location?.address?.line2 ? `, ${invoice.project.location?.address?.line2}` : "")
                        : invoice.project.location?.address?.line2
                        ? invoice.project.location?.address?.line2
                        : ""}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {invoice.project.location?.address?.city}, {invoice.project.location?.address?.state}
                      {invoice.project.location?.address?.postalCode !== "null" ? ` ${invoice.project.location?.address?.postalCode}` : ""}
                    </P>
                  </Flex>
                </Box>
                {invoice.project?.location?.id && (
                  <>
                    {/*
                     * Session 30: split the map section by viewer role.
                     * Admin sees the full SiteMapSection with attach
                     * controls. Client (non-admin) sees only the
                     * SnapshotSelectionDisplay, and only when snapshots
                     * are attached — otherwise the whole section
                     * (including divider) disappears. No orphan divider,
                     * no "Open map" 403 button, no metadata card for a
                     * map with no snapshots.
                     */}
                    {user.role !== null && (
                      <>
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                        <SiteMapSection
                          locationId={invoice.project.location.id}
                          mulchMapId={invoice.mulchMapId ?? null}
                          recordType="invoice"
                          recordId={invoice.id}
                          onAttachChanged={() => _init()}
                        />
                      </>
                    )}
                    {user.role === null && (invoice.mulchMapSnapshotIds?.length ?? 0) > 0 && (
                      <>
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                        <SnapshotSelectionDisplay
                          recordType="invoice"
                          recordId={invoice.id}
                          heading="Site maps included with this invoice"
                        />
                      </>
                    )}
                  </>
                )}
                {/*
                 * Maintenance Module §3.9 #18 (mockup 22) — Corrigo
                 * Invoice submission MOVED out of this sidebar to its own
                 * "Post to Corrigo" tab (A22). The panel now renders in the
                 * main content area; see the activeTab === "corrigo" block
                 * below. Nothing mounts here anymore.
                 */}
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Contacts
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Send To</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {`${invoice.client?.name}`}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {`${invoice.client?.billingEmail}`}
                    </P>
                  </Flex>
                  {user.role !== null && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>CC</Label>
                      {invoice.contactsCC?.length > 0 ? (
                        invoice.contactsCC.map((contact: any, index: number) => (
                          <P key={index} fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {`${contact}`}
                          </P>
                        ))
                      ) : (
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          -
                        </P>
                      )}
                    </Flex>
                  )}
                  {user.role !== null && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>BCC</Label>
                      {invoice.contactsBCC?.length > 0 ? (
                        invoice.contactsBCC.map((contact: any, index: number) => (
                          <P key={index} fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                            {`${contact}`}
                          </P>
                        ))
                      ) : (
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          -
                        </P>
                      )}
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Primary Contact</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {`${invoice.primaryClient?.name}`}
                    </P>
                  </Flex>
                </Box>
                {canApplyDiscount && (
                  <Box sx={{ paddingRight: "30px" }}>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"10px"}>
                      Payment discount
                    </H5>
                    <P fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"12px"} marginTop={"0px"} marginBottom={"12px"} lineHeight={"18px"}>
                      Grant the 5% early-payment discount even if this invoice falls outside the usual rules.
                    </P>
                    <BasicButton
                      scheme="secondaryOutline"
                      type="button"
                      keepText={true}
                      styles={{ margin: "0 0 20px 0", width: "100%" }}
                      onClick={() => setConfirmDiscountOpen(true)}
                    >
                      Apply payment discount
                    </BasicButton>
                  </Box>
                )}
                {invoice.status < 2 && user.role !== null && (
                  <Box sx={{ paddingBottom: "60px" }}>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                      Danger Zone
                    </H5>
                    <BasicButton
                      scheme="dangerSecondary"
                      styles={{ margin: ".25rem 0.625rem .25rem 0" }}
                      type="button"
                      onClick={() => confirmVoidInvoice()}
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor={themevals.colors.danger}
                      keepText={true}
                    >
                      <TrashIcon fill={"#A60000"} />
                      Void Invoice
                    </BasicButton>
                  </Box>
                )}
              </Box>
              {company && (
                <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
                  {/* A15 — consistent "back to work order" banner for an
                      invoice a Work Order spawned. Resolves the WO from
                      the invoice's quote's RFQ; NTE ceiling suppressed
                      here (an invoice is past the pricing-verification
                      step, so the ceiling is not the point). Renders
                      nothing for a plain invoice or a client viewer. */}
                  {user.role !== null && (
                    <MaintenanceContextBanner
                      rfqId={invoice.quote?.quoteRequest?.id ?? null}
                      showNte={false}
                    />
                  )}
                  {activeTab === "overview" && <Overview isCompany={user.role !== null ? true : false} company={company} invoice={invoice} reloadInvoice={reloadInvoice} />}
                  {/* {activeTab === "versions" && <Versions isCompany={user.role !== null ? true : false} />} */}
                  {activeTab === "notes" && <Notes isCompany={user.role !== null ? true : false} invoice={invoice} />}
                  {activeTab === "payments" && <Payments isCompany={user.role !== null ? true : false} invoice={invoice} reloadInvoice={reloadInvoice} />}
                  {/* A22 — Corrigo submission panel, now in the main
                      content area under its own tab (was crammed in the
                      sidebar). Same staff + plan-tagged gate as the tab
                      button; the panel self-no-ops if there are no
                      exportable lines. */}
                  {activeTab === "corrigo" &&
                    user.role !== null &&
                    invoice.quote?.quoteRequest?.servicePlanId && (
                      <CorrigoInvoiceSubmissionPanel invoice={invoice} />
                    )}
                </Box>
              )}
            </Grid>
          </>
        )}
      </Page>
      {confirmDiscountOpen && invoice && (
        <BasicModal
          title={"Apply payment discount?"}
          cancel_click={() => setConfirmDiscountOpen(false)}
          cancel_text="No, cancel"
          $submitting={applyingDiscount}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => _applyDiscount()}
              $submitting={applyingDiscount}
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {"Apply discount"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>
            {"5% off: " +
              new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(discountPreview.off) +
              ". The client will be charged " +
              new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(discountPreview.after) +
              ". This is also applied in Zoho."}
          </P>
        </BasicModal>
      )}
      {confirmVoidModalOpen && (
        <BasicModal
          title={`Void Invoice?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _voidInvoice()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Void invoice"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to void this invoice?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Invoice;
